import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

const NotificationSettings = () => {
    const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'senders'

    // Templates State
    const [templates, setTemplates] = useState([]);
    const [emailConfig, setEmailConfig] = useState({ enabled: false });
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);

    // Form state for Template editing
    const [editedSubject, setEditedSubject] = useState('');
    const [editedContent, setEditedContent] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [selectedSenderId, setSelectedSenderId] = useState('');

    // Senders State
    const [senders, setSenders] = useState([]);
    const [isEditingSender, setIsEditingSender] = useState(false);
    const [currentSender, setCurrentSender] = useState(null); // null = new, object = editing
    const [senderFormData, setSenderFormData] = useState({ name: '', email: '', is_default: false });
    const [savingSender, setSavingSender] = useState(false);

    // Common State
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [statistics, setStatistics] = useState({});

    // Dynamic height calculation (same pattern as OrderManagement.js)
    const contentAreaRef = useRef(null);
    const [scrollAreaHeight, setScrollAreaHeight] = useState(null);

    // Prevent browser window scrolling while on this page; restore on unmount
    useEffect(() => {
        const previousOverflowY = document.body.style.overflowY;
        document.body.style.overflowY = 'hidden';
        return () => {
            document.body.style.overflowY = previousOverflowY;
        };
    }, []);

    // Dynamically compute the available height for the scrollable content area
    useEffect(() => {
        const updateHeight = () => {
            if (!contentAreaRef.current) return;
            const rect = contentAreaRef.current.getBoundingClientRect();
            const padding = 12; // small bottom buffer
            const available = Math.max(200, window.innerHeight - rect.top - padding);
            setScrollAreaHeight(available);
        };

        // Initial calc + debounced recalc on DOM settle
        updateHeight();
        const timer = setTimeout(updateHeight, 100);
        window.addEventListener('resize', updateHeight);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateHeight);
        };
    }, [activeTab, loading]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Parallel fetch for efficiency
            const [templatesRes, configRes, logsRes, sendersRes] = await Promise.all([
                apiCall('/api/admin/email/templates'),
                apiCall('/api/admin/email/config'),
                apiCall('/api/admin/email/logs?limit=50'),
                apiCall('/api/admin/email/senders')
            ]);

            setTemplates(templatesRes.templates || []);
            setEmailConfig(configRes);
            setLogs(logsRes.logs || []);
            setStatistics(logsRes.statistics || {});
            setSenders(sendersRes.senders || []);

        } catch (error) {
            showErrorToast('Failed to load settings');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // --- Template Functions ---

    const toggleEmailSystem = async () => {
        try {
            const newEnabled = !emailConfig.enabled;
            await apiCall('/api/admin/email/config', {
                method: 'POST',
                body: JSON.stringify({ enabled: newEnabled })
            });
            setEmailConfig({ ...emailConfig, enabled: newEnabled });
            showSuccessToast(newEnabled ? 'Email system enabled' : 'Email system disabled');
        } catch (error) {
            showErrorToast('Failed to update email config');
        }
    };

    const selectTemplate = (template) => {
        setSelectedTemplate(template);
        setEditedSubject(template.subject);
        setEditedContent(template.content);
        setIsActive(template.active);
        setSelectedSenderId(template.sender_id || '');
        setEditMode(false);
    };

    const saveTemplate = async () => {
        if (!selectedTemplate) return;

        try {
            setSavingTemplate(true);
            await apiCall(`/api/admin/email/templates/${selectedTemplate.type}`, {
                method: 'PUT',
                body: JSON.stringify({
                    subject: editedSubject,
                    content: editedContent,
                    active: isActive,
                    sender_id: selectedSenderId || null
                })
            });

            showSuccessToast('Template updated successfully');
            setEditMode(false);

            // Update local state without full refetch
            setTemplates(prev => prev.map(t =>
                t.type === selectedTemplate.type
                    ? { ...t, subject: editedSubject, content: editedContent, active: isActive, sender_id: selectedSenderId }
                    : t
            ));

        } catch (error) {
            showErrorToast('Failed to save template');
        } finally {
            setSavingTemplate(false);
        }
    };

    const sendTestEmail = async () => {
        if (!selectedTemplate) return;

        try {
            setSendingTest(true);
            await apiCall(`/api/admin/email/templates/${selectedTemplate.type}/test`, {
                method: 'POST'
            });
            showSuccessToast('Test email sent! Check your inbox.');
        } catch (error) {
            showErrorToast('Failed to send test email');
        } finally {
            setSendingTest(false);
        }
    };

    const insertVariable = (variable) => {
        const textarea = document.getElementById('contentEditor');
        if (!textarea) return;

        const cursorPos = textarea.selectionStart;
        const textBefore = editedContent.substring(0, cursorPos);
        const textAfter = editedContent.substring(cursorPos);
        setEditedContent(textBefore + `{{${variable}}}` + textAfter);
    };

    // --- Sender Functions ---

    const openSenderModal = (sender = null) => {
        setCurrentSender(sender);
        if (sender) {
            setSenderFormData({
                name: sender.name,
                email: sender.email,
                is_default: sender.is_default
            });
        } else {
            setSenderFormData({ name: '', email: '', is_default: false });
        }
        setIsEditingSender(true);
    };

    const saveSender = async () => {
        try {
            setSavingSender(true);
            const method = currentSender ? 'PUT' : 'POST';
            const url = currentSender
                ? `/api/admin/email/senders/${currentSender.id}`
                : '/api/admin/email/senders';

            await apiCall(url, {
                method,
                body: JSON.stringify(senderFormData)
            });

            showSuccessToast(currentSender ? 'Sender updated' : 'Sender created');
            setIsEditingSender(false);

            // Refresh senders list
            const res = await apiCall('/api/admin/email/senders');
            setSenders(res.senders || []);

        } catch (error) {
            showErrorToast('Failed to save sender');
        } finally {
            setSavingSender(false);
        }
    };

    const deleteSender = async (senderId) => {
        if (!window.confirm('Are you sure you want to delete this sender? Templates using this sender will revert to default.')) return;

        try {
            await apiCall(`/api/admin/email/senders/${senderId}`, { method: 'DELETE' });
            showSuccessToast('Sender deleted');
            setSenders(prev => prev.filter(s => s.id !== senderId));
        } catch (error) {
            showErrorToast('Failed to delete sender');
        }
    };

    if (loading) {
        return (
            <div className="h-full bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col overflow-hidden bg-gray-50 px-4 pt-2 pb-1">
            <div className="max-w-7xl mx-auto flex-1 flex flex-col overflow-hidden min-h-0 w-full">
                {/* Header */}
                <div className="mb-1 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Email Notification Settings</h1>
                        <p className="text-gray-600 mt-0.5 text-sm">Manage email templates, senders, and configuration</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-2">
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`py-2 px-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'templates'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Email Templates
                    </button>
                    <button
                        onClick={() => setActiveTab('senders')}
                        className={`py-2 px-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'senders'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Sender Identities
                    </button>
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`py-2 px-4 font-medium text-sm transition-colors border-b-2 ${activeTab === 'logs'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Logs & Statistics
                    </button>
                </div>

                {activeTab === 'templates' && (
                    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                        {/* Global Email Toggle */}
                        <div className="bg-white rounded-lg shadow-sm p-3 mb-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900">System Master Switch</h2>
                                    <p className="text-gray-600 text-xs">Enable or disable all outgoing emails globally</p>
                                </div>
                                <button
                                    onClick={toggleEmailSystem}
                                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${emailConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${emailConfig.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        </div>

                        {/* Template Management Layout */}
                        <div
                            ref={contentAreaRef}
                            className="grid grid-cols-12 gap-3"
                            style={scrollAreaHeight ? { height: scrollAreaHeight } : {}}
                        >
                            {/* List */}
                            <div className="col-span-4 bg-white rounded-lg shadow-sm p-4 overflow-y-auto">
                                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Available Templates</h3>
                                <div className="space-y-2">
                                    {templates.map((template) => (
                                        <button
                                            key={template.type}
                                            onClick={() => selectTemplate(template)}
                                            className={`w-full text-left p-3 rounded-md border transition-all ${selectedTemplate?.type === template.type
                                                ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                                                : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-900 text-sm capitalize">
                                                        {template.type.replace(/_/g, ' ')}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {senders.find(s => s.id === template.sender_id)?.name || 'Default Sender'}
                                                    </p>
                                                </div>
                                                <div className={`mt-1 w-2 h-2 rounded-full ${template.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Editor */}
                            <div className="col-span-8 bg-white rounded-lg shadow-sm p-4 overflow-y-auto">
                                {selectedTemplate ? (
                                    <>
                                        <div className="flex items-center justify-between mb-6 pb-4 border-b">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-900 capitalize">
                                                    {selectedTemplate.type.replace(/_/g, ' ')}
                                                </h2>
                                                <p className="text-sm text-gray-500">Configure content and settings for this notification</p>
                                            </div>
                                            <div className="flex gap-2">
                                                {!editMode ? (
                                                    <>
                                                        <button
                                                            onClick={sendTestEmail}
                                                            disabled={sendingTest || !emailConfig.enabled}
                                                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                                                        >
                                                            {sendingTest ? 'Sending...' : 'Send Test'}
                                                        </button>
                                                        <button
                                                            onClick={() => setEditMode(true)}
                                                            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                                        >
                                                            Edit Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setEditMode(false);
                                                                selectTemplate(selectedTemplate); // Reset
                                                            }}
                                                            className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={saveTemplate}
                                                            disabled={savingTemplate}
                                                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            {savingTemplate ? 'Saving...' : 'Save Changes'}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {/* Configuration Row */}
                                            {editMode && (
                                                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                                                    <div>
                                                        <label className="flex items-center cursor-pointer mb-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={isActive}
                                                                onChange={(e) => setIsActive(e.target.checked)}
                                                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                                            />
                                                            <span className="ml-2 text-sm font-medium text-gray-700">Template Active</span>
                                                        </label>
                                                        <p className="text-xs text-gray-500 ml-6">Uncheck to disable this specific email type</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Sender Identity</label>
                                                        <select
                                                            value={selectedSenderId}
                                                            onChange={(e) => setSelectedSenderId(e.target.value)}
                                                            className="block w-full text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                        >
                                                            <option value="">Default (From .env defaults)</option>
                                                            {senders.map(sender => (
                                                                <option key={sender.id} value={sender.id}>
                                                                    {sender.name} &lt;{sender.email}&gt;
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Subject */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                                                {editMode ? (
                                                    <input
                                                        type="text"
                                                        value={editedSubject}
                                                        onChange={(e) => setEditedSubject(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                ) : (
                                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 font-medium">
                                                        {editedSubject}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center mb-1">
                                                    <label className="block text-sm font-medium text-gray-700">HTML Content</label>
                                                    {editMode && selectedTemplate.available_variables && (
                                                        <div className="flex gap-1 flex-wrap justify-end">
                                                            {selectedTemplate.available_variables.map((variable) => (
                                                                <button
                                                                    key={variable}
                                                                    onClick={() => insertVariable(variable)}
                                                                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 border border-gray-300"
                                                                    title="Click to insert"
                                                                >
                                                                    {`{{${variable}}}`}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {editMode ? (
                                                    <textarea
                                                        id="contentEditor"
                                                        value={editedContent}
                                                        onChange={(e) => setEditedContent(e.target.value)}
                                                        rows={12}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                                                    />
                                                ) : (
                                                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md h-64 overflow-y-auto">
                                                        <pre className="text-xs whitespace-pre-wrap font-mono text-gray-600">{editedContent}</pre>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                        <svg className="w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <p>Select a template to view details</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'senders' && (
                    <div
                        ref={contentAreaRef}
                        className="bg-white rounded-lg shadow-sm p-4 overflow-y-auto"
                        style={scrollAreaHeight ? { height: scrollAreaHeight } : {}}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-xl font-bold text-gray-900">Configured Senders</h2>
                            <button
                                onClick={() => openSenderModal()}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                            >
                                + Add New Sender
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {senders.map((sender) => (
                                <div key={sender.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{sender.name}</h3>
                                            <p className="text-sm text-gray-500">{sender.email}</p>
                                        </div>
                                        {sender.is_default && (
                                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Default</span>
                                        )}
                                    </div>
                                    <div className="mt-4 flex justify-end gap-2 pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => openSenderModal(sender)}
                                            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteSender(sender.id)}
                                            className="text-red-600 hover:text-red-800 text-sm font-medium ml-2"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {senders.length === 0 && (
                                <div className="col-span-full py-12 text-center text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                    <p>No custom senders configured.</p>
                                    <p className="text-sm mt-1">System is using configured environment variables.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'logs' && (
                    <div
                        ref={contentAreaRef}
                        className="bg-white rounded-lg shadow-sm p-4 flex flex-col overflow-hidden"
                        style={scrollAreaHeight ? { height: scrollAreaHeight } : {}}
                    >
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="text-sm text-blue-600 font-medium">Total Emails</p>
                                <p className="text-2xl font-bold text-blue-900">{statistics.total || 0}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <p className="text-sm text-green-600 font-medium">Successfully Sent</p>
                                <p className="text-2xl font-bold text-green-900">{statistics.sent || 0}</p>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg">
                                <p className="text-sm text-red-600 font-medium">Failed</p>
                                <p className="text-2xl font-bold text-red-900">{statistics.failed || 0}</p>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto min-h-0">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {logs.map((log, idx) => (
                                        <tr key={idx}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {log.template_type}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {log.recipient}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.status === 'sent' ? 'bg-green-100 text-green-800' :
                                                    log.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {log.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Sender Modal */}
            {isEditingSender && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold mb-4">{currentSender ? 'Edit Sender' : 'Add New Sender'}</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                                <input
                                    type="text"
                                    value={senderFormData.name}
                                    onChange={(e) => setSenderFormData({ ...senderFormData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. Sales Team"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    value={senderFormData.email}
                                    onChange={(e) => setSenderFormData({ ...senderFormData, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. sales@matrica.in"
                                />
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={senderFormData.is_default}
                                    onChange={(e) => setSenderFormData({ ...senderFormData, is_default: e.target.checked })}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-900">Set as default sender</label>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setIsEditingSender(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveSender}
                                disabled={savingSender || !senderFormData.name || !senderFormData.email}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {savingSender ? 'Saving...' : 'Save Sender'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationSettings;
